/**
 * Context provider for the Warehouse Layout Editor.
 * Provides centralized state, dispatch, and derived data to all child components.
 */

import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import {
  LayoutEditorState,
  LayoutAction,
  useLayoutReducer,
} from '../hooks/useLayoutReducer';
import { RackDimensions, RackSummary, WarehouseLayout } from '../types/warehouseLayout';
import {
  getLayout,
  saveLayout,
  createEmptyLayout,
} from '../utils/layoutStorage';
import {
  calculatePreviewPositions,
} from '../utils/bulkPlacement';
import { calculateRackDimensions, getRotatedDimensions } from '../utils/positionCalculator';
import { stcBinService } from '../../../../../services/stcBinService';

interface LayoutEditorContextValue {
  state: LayoutEditorState;
  dispatch: React.Dispatch<LayoutAction>;
  rackDimensionsMap: Map<string, RackDimensions>;
  canUndo: boolean;
  canRedo: boolean;
  save: () => void;
  warehouseId: string;
  companyNo: string;
}

const LayoutEditorContext = createContext<LayoutEditorContextValue | null>(null);

interface LayoutEditorProviderProps {
  warehouseId: string;
  companyNo: string;
  children: React.ReactNode;
}

export const LayoutEditorProvider: React.FC<LayoutEditorProviderProps> = ({
  warehouseId,
  companyNo,
  children,
}) => {
  const { state, dispatch, rackDimensionsMap, canUndo, canRedo } = useLayoutReducer();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [warehouseId]);

  /**
   * Auto-fit warehouse dimensions to contain all placed racks with comfortable margins.
   * Only adjusts if the current dimensions are too large relative to rack footprint.
   */
  const autoFitDimensions = (layout: WarehouseLayout, summaries: RackSummary[]): WarehouseLayout => {
    if (layout.racks.length === 0) return layout;

    // Build dimensions map from summaries
    const dimMap = new Map<string, RackDimensions>();
    summaries.forEach(s => dimMap.set(s.rackNumber, calculateRackDimensions(s)));

    // Calculate bounding box of all placed racks
    let maxX = 0, maxY = 0;
    layout.racks.forEach(rack => {
      const dim = dimMap.get(rack.rackNumber);
      if (!dim) return;
      const rotated = getRotatedDimensions(dim, rack.rotation);
      maxX = Math.max(maxX, rack.x + rotated.width / 2);
      maxY = Math.max(maxY, rack.y + rotated.depth / 2);
    });

    // Add margin (3m on each side for aisles and workspace)
    const MARGIN = 3;
    const neededLength = Math.ceil(maxX + MARGIN);
    const neededWidth = Math.ceil(maxY + MARGIN);

    const currentArea = layout.dimensions.length * layout.dimensions.width;
    const neededArea = neededLength * neededWidth;

    // Shrink if current dimensions are more than 2.5× larger than needed
    if (currentArea > neededArea * 2.5) {
      return {
        ...layout,
        dimensions: {
          ...layout.dimensions,
          length: Math.max(neededLength, 15), // Minimum 15m
          width: Math.max(neededWidth, 10),   // Minimum 10m
        },
      };
    }

    // Grow if racks extend beyond current dimensions
    if (neededLength > layout.dimensions.length || neededWidth > layout.dimensions.width) {
      return {
        ...layout,
        dimensions: {
          ...layout.dimensions,
          length: Math.max(layout.dimensions.length, neededLength),
          width: Math.max(layout.dimensions.width, neededWidth),
        },
      };
    }

    return layout;
  };

  const loadData = async () => {
    dispatch({ type: 'LOAD_DATA_START' });
    try {
      const summaries = await stcBinService.getRackSummary(companyNo, warehouseId);
      let storedLayout = getLayout(warehouseId);
      if (!storedLayout) {
        storedLayout = createEmptyLayout(warehouseId, `Lager ${warehouseId}`);
      }
      // Auto-fit dimensions if warehouse is oversized relative to rack footprint
      storedLayout = autoFitDimensions(storedLayout, summaries);
      dispatch({ type: 'LOAD_DATA_SUCCESS', layout: storedLayout, rackSummaries: summaries });
    } catch (err) {
      console.error('Error loading data:', err);
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: 'Kunne ikke laste lagerdata',
        fallbackLayout: createEmptyLayout(warehouseId),
      });
    }
  };

  // Auto-save on layout changes
  useEffect(() => {
    if (state.layout && !state.loading) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        save();
      }, 2000);
    }
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [state.layout]);

  // Update preview positions when placement config or anchor changes
  useEffect(() => {
    if (state.layout && state.editorState.anchorRack && state.selectedUnplacedRacks.length > 0) {
      const anchorRack = state.layout.racks.find(r => r.rackNumber === state.editorState.anchorRack);
      if (anchorRack) {
        const previews = calculatePreviewPositions(
          anchorRack,
          state.selectedUnplacedRacks,
          rackDimensionsMap,
          state.layout.racks,
          state.layout.dimensions,
          state.editorState.placementConfig,
          state.layout.gridUnit
        );
        dispatch({ type: 'SET_PREVIEW_POSITIONS', positions: previews });
      }
    } else {
      dispatch({ type: 'SET_PREVIEW_POSITIONS', positions: [] });
    }
  }, [
    state.layout?.racks,
    state.editorState.anchorRack,
    state.editorState.placementConfig,
    state.selectedUnplacedRacks,
    rackDimensionsMap,
  ]);

  const save = useCallback(() => {
    if (!state.layout) return;
    try {
      dispatch({ type: 'SAVE_START' });
      saveLayout(state.layout);
      dispatch({ type: 'SAVE_SUCCESS' });
      setTimeout(() => dispatch({ type: 'SAVE_SUCCESS' }), 100);
    } catch (err) {
      console.error('Error saving layout:', err);
      dispatch({ type: 'SAVE_FAILURE' });
    }
  }, [state.layout]);

  return (
    <LayoutEditorContext.Provider
      value={{
        state,
        dispatch,
        rackDimensionsMap,
        canUndo,
        canRedo,
        save,
        warehouseId,
        companyNo,
      }}
    >
      {children}
    </LayoutEditorContext.Provider>
  );
};

export function useLayoutEditor(): LayoutEditorContextValue {
  const ctx = useContext(LayoutEditorContext);
  if (!ctx) {
    throw new Error('useLayoutEditor must be used within a LayoutEditorProvider');
  }
  return ctx;
}
